import { Link } from "react-router-dom";
import { ArrowRight, HandHeart, Leaf, Sparkles, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ProductCard } from "../components/ProductCard";
import { TextileVisual } from "../components/TextileVisual";
import { InquiryForm } from "../components/Forms";
import { artisanStories, founderQuote, giftingUseCases, productPhotography, whyBuyPoints } from "../data/siteContent";

export default function Home({ products, categories, impact, onAddToCart, onWhatsAppOrder }) {
  const totes = products.filter((product) => product.category_slug === "tote-bags").slice(0, 3);
  const heroProduct = products.find((product) => product.hero) || products[0];

  return (
    <>
      <section className="hero-section" data-testid="home-hero-section">
        <div className="hero-section__media">
          <TextileVisual visualKey="hero" label="Project Umeed textile hero visual" testId="home-hero-textile-visual" />
        </div>
        <div className="hero-section__content">
          <Badge className="umeed-badge" data-testid="home-hero-badge">Ethical luxury · Women-led · Kutch</Badge>
          <h1 data-testid="home-hero-heading">Handcrafted by Women. Rooted in Kutch. Made for the World.</h1>
          <p data-testid="home-hero-subheadline">Project Umeed brings premium, sustainable, handcrafted textile products made by women artisans of Kutch to conscious buyers, gifting customers, and global markets.</p>
          <div className="hero-actions">
            <Button className="btn-primary" asChild data-testid="home-shop-tote-button"><Link to="/category/tote-bags">Shop Tote Bags <ArrowRight aria-hidden="true" /></Link></Button>
            <Button className="btn-quiet" variant="outline" asChild data-testid="home-meet-women-button"><Link to="/meet-the-artisans">Meet the Women Behind Umeed</Link></Button>
            <Button className="btn-quiet" variant="outline" asChild data-testid="home-gifting-button"><Link to="/corporate-gifting">Explore Conscious Gifting</Link></Button>
          </div>
          <div className="hero-metrics" data-testid="home-hero-metrics">
            <span data-testid="hero-metric-artisans">20 women artisans</span>
            <span data-testid="hero-metric-categories">4 product categories</span>
            <span data-testid="hero-metric-origin">Kutch to global markets</span>
          </div>
        </div>
      </section>

      <section className="story-section section-pad" data-testid="brand-story-section">
        <div className="section-kicker" data-testid="brand-story-kicker">Founded by Sara</div>
        <div className="two-column">
          <div>
            <h2 data-testid="brand-story-heading">A premium craft label built to create lasting livelihood access.</h2>
          </div>
          <div className="rich-copy">
            <p data-testid="brand-story-copy">Many women artisans have the skill, culture, and craft. What they often lack is consistent income, digital visibility, and direct access to modern buyers. Project Umeed exists to bridge that gap with dignity, design and market access.</p>
            <div className="support-grid" data-testid="sara-support-grid">
              {["Design direction", "Product development", "Photography", "Instagram visibility", "Sales support", "Logistics", "Packaging", "Storytelling", "Customer access", "Pricing guidance", "Digital market reach"].map((item) => (
                <span key={item} data-testid={`sara-support-${item.toLowerCase().replaceAll(" ", "-")}`}>{item}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="movement-band section-pad" data-testid="movement-section">
        <div>
          <p className="section-kicker" data-testid="movement-kicker">This Is More Than Craft</p>
          <h2 data-testid="movement-heading">This Is Access.</h2>
          <p data-testid="movement-copy">Every Project Umeed product helps women artisans move closer to identity, independence, and access to markets they were previously disconnected from. Through digital platforms, thoughtful design, and premium positioning, local craft can travel beyond geography.</p>
        </div>
        <Button className="btn-primary" asChild data-testid="movement-cta-button"><Link to="/impact">Become Part of the Movement</Link></Button>
      </section>

      <section className="section-pad" data-testid="home-tote-section">
        <div className="section-heading-row">
          <div>
            <p className="section-kicker" data-testid="tote-section-kicker">Hero Product</p>
            <h2 data-testid="tote-section-heading">The Umeed Tote</h2>
            <p data-testid="tote-section-copy">A tote bag from Project Umeed is not just an everyday essential. It is a piece of Kutch-inspired textile culture, handcrafted by women artisans and designed for modern conscious living.</p>
          </div>
          <Button className="btn-quiet" variant="outline" asChild data-testid="tote-section-shop-link"><Link to="/category/tote-bags">Shop Tote Bags</Link></Button>
        </div>
        <div className="product-grid" data-testid="home-tote-products-grid">
          {totes.map((product) => <ProductCard key={product.slug} product={product} featured={product.slug === heroProduct?.slug} onAddToCart={onAddToCart} onWhatsAppOrder={onWhatsAppOrder} />)}
        </div>
      </section>

      <section className="category-section section-pad" data-testid="home-category-section">
        <div className="section-heading-row">
          <div>
            <p className="section-kicker" data-testid="category-section-kicker">The Collection</p>
            <h2 data-testid="category-section-heading">Handmade products for daily life and meaningful gifting.</h2>
          </div>
          <Button className="btn-primary" asChild data-testid="category-shop-all-button"><Link to="/shop">Shop All</Link></Button>
        </div>
        <div className="category-grid" data-testid="category-grid">
          {categories.map((category) => (
            <Link className="category-card" key={category.slug} to={`/category/${category.slug}`} data-testid={`category-card-${category.slug}`}>
              <TextileVisual visualKey={category.visual_key} label={`${category.name} textile visual`} testId={`category-visual-${category.slug}`} />
              <Badge className="umeed-badge" data-testid={`category-story-tag-${category.slug}`}>{category.story_tag}</Badge>
              <h3 data-testid={`category-name-${category.slug}`}>{category.name}</h3>
              <p data-testid={`category-description-${category.slug}`}>{category.tagline}</p>
              <span data-testid={`category-shop-button-${category.slug}`}>Shop {category.name} <ArrowRight aria-hidden="true" /></span>
            </Link>
          ))}
        </div>
      </section>

      <section className="photo-gallery-section section-pad" data-testid="product-photo-gallery-section">
        <div className="section-heading-row">
          <div>
            <p className="section-kicker" data-testid="photo-gallery-kicker">Product Gallery</p>
            <h2 data-testid="photo-gallery-heading">Signature handmade totes, ready to browse.</h2>
          </div>
        </div>
        <div className="photo-gallery-grid" data-testid="product-photo-gallery-grid">
          {productPhotography.map((image, index) => (
            <figure className="photo-gallery-item" key={image.src} data-testid={`product-photo-gallery-item-${index}`}>
              <img src={image.src} alt={image.alt} loading="lazy" />
            </figure>
          ))}
        </div>
      </section>

      <section className="why-section section-pad" data-testid="why-buy-section">
        <div className="section-heading-row">
          <div>
            <p className="section-kicker" data-testid="why-buy-kicker">Why Buy From Project Umeed</p>
            <h2 data-testid="why-buy-heading">Why Conscious Buyers Choose Project Umeed</h2>
          </div>
        </div>
        <div className="why-grid" data-testid="why-buy-points-grid">
          {whyBuyPoints.map((point, index) => (
            <div key={point} className="why-point" data-testid={`why-buy-point-${index}`}>
              <HandHeart aria-hidden="true" />
              <span>{point}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="artisan-section section-pad" data-testid="artisan-impact-section">
        <div className="section-heading-row">
          <div>
            <p className="section-kicker" data-testid="artisan-impact-kicker">Artisan Impact</p>
            <h2 data-testid="artisan-impact-heading">Behind Every Product Is a Woman Building Her Future.</h2>
          </div>
          <Button className="btn-quiet" variant="outline" asChild data-testid="artisan-impact-link"><Link to="/meet-the-artisans">Meet the Artisans</Link></Button>
        </div>
        <div className="artisan-grid" data-testid="artisan-impact-grid">
          {artisanStories.slice(0, 3).map((artisan) => (
            <article className="artisan-card" key={artisan.name} data-testid={`artisan-card-${artisan.name.toLowerCase().replaceAll(" ", "-")}`}>
              <Users aria-hidden="true" />
              <h3 data-testid={`artisan-name-${artisan.name.toLowerCase().replaceAll(" ", "-")}`}>{artisan.name}</h3>
              <p data-testid={`artisan-skill-${artisan.name.toLowerCase().replaceAll(" ", "-")}`}>{artisan.skill}</p>
              <span data-testid={`artisan-contribution-${artisan.name.toLowerCase().replaceAll(" ", "-")}`}>{artisan.contribution}</span>
              <p data-testid={`artisan-story-${artisan.name.toLowerCase().replaceAll(" ", "-")}`}>{artisan.story}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="sustainability-preview section-pad" data-testid="home-sustainability-preview">
        <Leaf aria-hidden="true" />
        <h2 data-testid="home-sustainability-heading">Sustainability That Feels Personal</h2>
        <p data-testid="home-sustainability-copy">Project Umeed believes sustainability is not just about materials. It is also about people, livelihoods, culture, and longevity.</p>
        <Button className="btn-primary" asChild data-testid="home-sustainability-link"><Link to="/sustainability">Explore Sustainability</Link></Button>
      </section>

      <section className="gifting-section section-pad" data-testid="home-gifting-section">
        <div>
          <p className="section-kicker" data-testid="home-gifting-kicker">Corporate & Conscious Gifting</p>
          <h2 data-testid="home-gifting-heading">Gifts That Carry Meaning</h2>
          <p data-testid="home-gifting-copy">For companies and individuals who want their gifts to feel premium, personal, ethical, and memorable, Project Umeed offers handcrafted textile products that carry both beauty and impact.</p>
          <div className="gifting-tags" data-testid="home-gifting-tags">
            {giftingUseCases.slice(0, 6).map((item) => <span key={item} data-testid={`gifting-use-${item.toLowerCase().replaceAll(" ", "-")}`}>{item}</span>)}
          </div>
        </div>
        <InquiryForm type="gifting" title="Enquire for Gifting" description="Share your occasion, quantity and timeline." testPrefix="home-gifting" />
      </section>

      <section className="founder-section section-pad" data-testid="home-founder-section">
        <div className="founder-note">
          <Sparkles aria-hidden="true" />
          <p className="section-kicker" data-testid="founder-kicker">A Note from Sara</p>
          <blockquote data-testid="founder-quote">“{founderQuote}”</blockquote>
          <Button className="btn-quiet" variant="outline" asChild data-testid="founder-story-link"><Link to="/founders-note">Read Sara’s Story</Link></Button>
        </div>
      </section>

      <section className="impact-strip section-pad" data-testid="home-impact-section">
        <div>
          <p className="section-kicker" data-testid="impact-kicker">Impact You Can Carry</p>
          <h2 data-testid="impact-heading">Every product creates visibility, income opportunity and long-term dignity.</h2>
        </div>
        <div className="impact-metrics" data-testid="impact-metrics-grid">
          {impact.metrics?.map((metric) => (
            <div key={metric.label} data-testid={`impact-metric-${metric.label.toLowerCase().replaceAll(" ", "-")}`}>
              <strong>{metric.value}</strong>
              <span>{metric.label}</span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
